import { useContext } from 'react';
import {
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import AuthContext from 'contexts/AuthContext';
import { toast } from 'shared/toast';

import { OrganizationUser } from 'types/access-control/organization';

import {
  Opportunity,
  OpportunityFile,
  OpportunityFilters,
  OpportunityFlowStatusAction,
  OpportunityFlowSummary,
  OpportunityHistoryEvent,
  OpportunityHistoryPage,
  OpportunityOrigin,
  OpportunityPayload,
} from 'types/opportunity';

import {
  chooseOpportunityFlowAction,
  createOpportunity,
  createOpportunityHistoryNote,
  deleteOpportunity,
  deleteOpportunityFile,
  fetchOpportunity,
  fetchOpportunityFiles,
  fetchOpportunityFlowStatusActions,
  fetchOpportunityFlowSummary,
  fetchOpportunityHistory,
  fetchOpportunityOrigins,
  fetchOrganizationUsers,
  setOpportunityFlowStatus,
  updateOpportunity,
  uploadOpportunityFile,
} from './service';

export const opportunityKeys = {
  all: ['opportunity'] as const,
  flowSummary: (
    organizationId: number | undefined,
    filters: OpportunityFilters,
    includeOpportunities: boolean,
    sort?: string,
  ) =>
    [
      ...opportunityKeys.all,
      'flow-summary',
      organizationId,
      filters,
      includeOpportunities,
      sort,
    ] as const,
  detail: (id: number) => [...opportunityKeys.all, 'detail', id] as const,
  files: (id: number) => [...opportunityKeys.all, 'files', id] as const,
  history: (id: number) => [...opportunityKeys.all, 'history', id] as const,
  flowStatusActions: (flowStatusId: number) =>
    [...opportunityKeys.all, 'flow-status-actions', flowStatusId] as const,
  origins: () => [...opportunityKeys.all, 'origins'] as const,
  organizationUsers: (organizationId: number | undefined) =>
    [...opportunityKeys.all, 'organization-users', organizationId] as const,
};

type UseOpportunityFlowSummaryParams = {
  filters?: OpportunityFilters;
  includeOpportunities?: boolean;
  sort?: string;
};

export const useOpportunityFlowSummary = ({
  filters = {},
  includeOpportunities = false,
  sort,
}: UseOpportunityFlowSummaryParams = {}): UseQueryResult<
  OpportunityFlowSummary[]
> => {
  const { user } = useContext(AuthContext);
  const organizationId = user?.managed_organization?.organization?.id;

  return useQuery(
    opportunityKeys.flowSummary(
      organizationId,
      filters,
      includeOpportunities,
      sort,
    ),
    () =>
      fetchOpportunityFlowSummary({
        organizationId: organizationId as number,
        filters,
        includeOpportunities,
        sort,
      }),
    { enabled: Boolean(organizationId) },
  );
};

export const useOpportunity = (
  id: number | undefined,
): UseQueryResult<Opportunity> =>
  useQuery(
    opportunityKeys.detail(id as number),
    () => fetchOpportunity(id as number),
    { enabled: Boolean(id) },
  );

export const useOpportunityOrigins = (): UseQueryResult<OpportunityOrigin[]> =>
  useQuery(opportunityKeys.origins(), fetchOpportunityOrigins, {
    staleTime: 1000 * 60 * 5,
  });

export const useOrganizationUsers = ({
  enabled = true,
}: { enabled?: boolean } = {}): UseQueryResult<OrganizationUser[]> => {
  const { user } = useContext(AuthContext);
  const organizationId = user?.managed_organization?.organization?.id;

  return useQuery(
    opportunityKeys.organizationUsers(organizationId),
    () => fetchOrganizationUsers(organizationId as number),
    {
      enabled: enabled && Boolean(organizationId),
      staleTime: 1000 * 60 * 5,
      onError: (err: { response?: { data?: { message?: string } } }) =>
        toast({
          status: 'error',
          description:
            err.response?.data?.message ||
            'Houve um erro ao tentar carregar os usuários da organização.',
        }),
    },
  );
};

export const useCreateOpportunity = (): UseMutationResult<
  Opportunity,
  unknown,
  OpportunityPayload
> => {
  const queryClient = useQueryClient();

  return useMutation(createOpportunity, {
    onSuccess: () => queryClient.invalidateQueries(opportunityKeys.all),
  });
};

export const useUpdateOpportunity = (): UseMutationResult<
  Opportunity,
  unknown,
  { id: number; payload: OpportunityPayload }
> => {
  const queryClient = useQueryClient();

  return useMutation(({ id, payload }) => updateOpportunity(id, payload), {
    onSuccess: () => queryClient.invalidateQueries(opportunityKeys.all),
  });
};

export const useDeleteOpportunity = (): UseMutationResult<
  void,
  unknown,
  number
> => {
  const queryClient = useQueryClient();

  return useMutation(deleteOpportunity, {
    onSuccess: () => queryClient.invalidateQueries(opportunityKeys.all),
  });
};

export const useOpportunityFiles = (
  opportunityId: number | undefined,
): UseQueryResult<OpportunityFile[]> =>
  useQuery(
    opportunityKeys.files(opportunityId as number),
    () => fetchOpportunityFiles(opportunityId as number),
    {
      enabled: Boolean(opportunityId),
      onError: (err: { response?: { data?: { message?: string } } }) =>
        toast({
          status: 'error',
          description:
            err.response?.data?.message ||
            'Houve um erro ao tentar carregar os anexos da oportunidade.',
        }),
    },
  );

export const useUploadOpportunityFile = (): UseMutationResult<
  OpportunityFile,
  unknown,
  { opportunityId: number; file: File }
> => {
  const queryClient = useQueryClient();

  return useMutation(uploadOpportunityFile, {
    onSuccess: () => queryClient.invalidateQueries(opportunityKeys.all),
  });
};

export const useDeleteOpportunityFile = (): UseMutationResult<
  void,
  unknown,
  { opportunityId: number; fileId: number }
> => {
  const queryClient = useQueryClient();

  return useMutation(deleteOpportunityFile, {
    onSuccess: () => queryClient.invalidateQueries(opportunityKeys.all),
  });
};

export const useOpportunityHistory = (
  opportunityId: number,
): UseInfiniteQueryResult<OpportunityHistoryPage> =>
  useInfiniteQuery(
    opportunityKeys.history(opportunityId),
    ({ pageParam = 1 }) =>
      fetchOpportunityHistory({ opportunityId, page: pageParam }),
    {
      getNextPageParam: ({ meta }) =>
        meta && meta.current_page < meta.last_page
          ? meta.current_page + 1
          : undefined,
    },
  );

export const useCreateOpportunityHistoryNote = (): UseMutationResult<
  OpportunityHistoryEvent,
  unknown,
  { opportunityId: number; message: string }
> => {
  const queryClient = useQueryClient();

  return useMutation(createOpportunityHistoryNote, {
    onSuccess: (_, { opportunityId }) =>
      queryClient.invalidateQueries(opportunityKeys.history(opportunityId)),
  });
};

export const useOpportunityFlowStatusActions = (
  flowStatusId: number | null | undefined,
): UseQueryResult<OpportunityFlowStatusAction[]> =>
  useQuery(
    opportunityKeys.flowStatusActions(flowStatusId as number),
    () => fetchOpportunityFlowStatusActions(flowStatusId as number),
    { enabled: Boolean(flowStatusId) },
  );

type MoveOpportunityParams = {
  opportunityId: number;
  flowStatusId: number;
  actionId?: number;
  message: string;
};

// Com ação, segue o fluxo (gera histórico); sem ação, só tira de "Sem status".
export const useMoveOpportunity = (): UseMutationResult<
  void,
  unknown,
  MoveOpportunityParams
> => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ opportunityId, flowStatusId, actionId, message }) =>
      actionId
        ? chooseOpportunityFlowAction({ actionId, opportunityId, message })
        : setOpportunityFlowStatus({ opportunityId, flowStatusId }),
    { onSuccess: () => queryClient.invalidateQueries(opportunityKeys.all) },
  );
};
